import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { useGroupsByType } from 'app/network/groups';
import type { Loan } from 'app/types/loan';
import { useTranslation } from 'react-i18next';
import { ListingThumbnail } from './listing-thumbnail';
import { useUser } from 'app/network/user';
import { ClubName } from 'app/components/club-name';
import { LoanStatusRow } from 'app/components/loan-status-row';
import { getLoanType, LoanStatus, LoanType } from 'app/types/loan';
import type { PressableProps } from 'react-native';
import { Pressable, View } from 'react-native';
import { localeAwareFormat } from 'app/lib/date';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { GroupType } from 'app/types/group-base';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import type { Theme } from 'app/shopify';
import { IconClock } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';

const activitySectorFeatureFlag = FeatureFlag.useActivitySector;

interface LoanCardProps extends Loan {
  calendarHighlighted?: boolean;
  calendarSelected?: boolean;
  onCalendarBlur?: PressableProps['onBlur'];
  onCalendarFocus?: PressableProps['onFocus'];
  onCalendarHoverIn?: PressableProps['onHoverIn'];
  onCalendarHoverOut?: PressableProps['onHoverOut'];
  onCalendarSelect?: (loan: Loan) => void;
  onPress?: (loan: Loan) => void;
}

type LoanUser = Loan['borrower'];
type LoanGroup = NonNullable<LoanUser['memberships']>[number]['group'];

function getLoanCardDetails(loan: Loan, currentUser: ReturnType<typeof useUser>['data']) {
  const loanType = getLoanType(loan.borrower, currentUser);
  const otherUser = loanType === LoanType.Borrowing ? loan.lender : loan.borrower;
  return {
    isDonation: isDonationListing(loan.listing),
    isMyListing: currentUser?.id === loan.listing.userId,
    isService: loan.listing.subtype === ListingSubtype.service,
    loanType,
    otherUser,
    otherUserGroups: otherUser.memberships?.map((membership) => membership.group),
  };
}

function LoanPastHeader({ loan, loanType, tPrefix }: { loan: Loan; loanType: LoanType; tPrefix: string }) {
  const { t } = useTranslation('loans');
  if (loan.status !== LoanStatus.Completed && loan.status !== LoanStatus.Returned) return null;
  const endDate = loan.returned ?? loan.end;
  const singleDate = new Date(loan.start).toDateString() === new Date(endDate).toDateString();
  const localizedStart = localeAwareFormat(new Date(loan.start), t('dateFormat_MMMMd', { ns: 'common' }));
  const localizedEnd = localeAwareFormat(new Date(endDate), t('dateFormat_MMMMdYYYY', { ns: 'common' }));
  return (
    <Row alignItems="center" marginBottom="spacing.general.x4" style={{ flexDirection: 'row', flex: 1 }}>
      <Text flex={1} fontWeight="600" variant="bodyBase">
        {t(`${tPrefix}${loanType}.pastTitle`)}
      </Text>
      <Row gap="spacing.general.x1" style={{ marginLeft: 'auto' }}>
        <IconClock strokeWidth={ICON_STROKE_WIDTH} />
        <Text variant="bodyBase">
          {t(singleDate ? 'past.cards.date' : 'past.cards.dates', { start: localizedStart, end: localizedEnd })}
        </Text>
      </Row>
    </Row>
  );
}

function LoanCardContent({
  activitySectorTagsEnabled,
  displayedGroups,
  isDonation,
  isMyListing,
  isService,
  loan,
  loanType,
  otherUser,
}: {
  activitySectorTagsEnabled: boolean;
  displayedGroups?: LoanGroup[];
  isDonation: boolean;
  isMyListing: boolean;
  isService: boolean;
  loan: Loan;
  loanType: LoanType;
  otherUser: LoanUser;
}) {
  const { t } = useTranslation('loans');
  const theme = useTheme<Theme>();
  const isSell = loan.listing.transactionMode === TransactionMode.sell;
  const tPrefix = isSell ? 'sales.' : isService ? 'services.' : isDonation ? 'donations.' : '';

  return (
    <Column style={{ width: '100%' }}>
      <LoanPastHeader loan={loan} loanType={loanType} tPrefix={tPrefix} />
      <Row alignItems="center" gap="spacing.general.x2" marginBottom="spacing.general.x4">
        {otherUser.profile ? (
          <PCImage
            aspectRatio={1}
            height={!displayedGroups?.length ? 25 : 35}
            source={{ uri: otherUser.profile.picture }}
            style={{ borderRadius: theme.borderRadii['radius.xl'] }}
          />
        ) : null}
        <Column flex={1} minWidth={0}>
          <Text fontSize={16} fontWeight="600" numberOfLines={1} variant="bodyBase">
            {otherUser.profile
              ? `${otherUser.profile.firstName} ${otherUser.profile.lastName}`
              : t('deletedUser', { ns: 'common' })}
          </Text>
          <ClubName
            asTags={activitySectorTagsEnabled}
            groups={displayedGroups}
            preventNavigateToClubMembers
            smaller={!activitySectorTagsEnabled}
            tagBackgroundColor="surface.tertiary-neutral"
          />
        </Column>
      </Row>
      <Text fontSize={12} marginBottom="spacing.general.x1" numberOfLines={1} variant="bodyBase">
        {t(`card.type.${loan.listing.type}.${isMyListing ? 'mine' : 'notMine'}`)}
      </Text>
      <Row alignItems="center" gap="spacing.general.x2">
        {loan.listing.type === ListingType.offer ? (
          <ListingThumbnail hideCorpoBadge isFound={Boolean(loan.listing.lender)} size={40} {...loan.listing} />
        ) : null}
        <Text flex={1} flexShrink={1} fontWeight="600" numberOfLines={2} variant="bodyBase">
          {loan.listing.item.name}
        </Text>
      </Row>
      {loan.status !== LoanStatus.Completed ? (
        <View style={{ marginTop: theme.spacing['spacing.general.x4'] }}>
          <LoanStatusRow
            end={loan.end}
            isDonation={isDonation}
            listingSubtype={loan.listing.subtype}
            returned={loan.returned}
            showClock
            start={loan.start}
            status={loan.status}
            transactionMode={loan.listing.transactionMode}
          />
        </View>
      ) : null}
    </Column>
  );
}

export function LoanCard({
  calendarHighlighted = false,
  calendarSelected = false,
  onCalendarBlur,
  onCalendarFocus,
  onCalendarHoverIn,
  onCalendarHoverOut,
  onCalendarSelect,
  onPress,
  ...loan
}: LoanCardProps) {
  const theme = useTheme<Theme>();
  const navigation = useNavigation();
  const userQuery = useUser();
  const activitySectorTagsEnabled = isFeatureEnabled(activitySectorFeatureFlag, userQuery.data?.featureFlags);
  const sectorGroupsQuery = useGroupsByType(GroupType.sector, { enabled: activitySectorTagsEnabled });

  const { isMyListing, loanType, otherUser, otherUserGroups, isService, isDonation } = getLoanCardDetails(
    loan,
    userQuery.data,
  );

  const displayedGroups = activitySectorTagsEnabled
    ? sectorGroupsQuery.data === undefined
      ? otherUserGroups
      : otherUserGroups?.filter((group) => sectorGroupsQuery.data.some((sector) => sector.id === group.id))
    : otherUserGroups;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: calendarSelected }}
      onBlur={onCalendarBlur}
      onFocus={onCalendarFocus}
      onHoverIn={onCalendarHoverIn}
      onHoverOut={onCalendarHoverOut}
      onPress={() => {
        onCalendarSelect?.(loan);
        if (onPress) {
          onPress(loan);
        } else {
          navigateToLoan(navigation, loan.id, loan.listing.id);
        }
      }}
      style={({ pressed }) => ({
        backgroundColor:
          calendarSelected || calendarHighlighted ? theme.colors['surface.selected'] : theme.colors['surface.default'],
        padding: theme.spacing['spacing.general.x4'],
        borderRadius: theme.borderRadii['radius.lg'],
        borderWidth: calendarSelected ? 2 : 1,
        borderColor: calendarSelected
          ? theme.colors['surface.brand.default']
          : calendarHighlighted
            ? theme.colors['surface.brand.strong']
            : theme.colors['surface.disabled'],
        opacity: pressed ? 0.72 : 1,
        width: '100%',
      })}
    >
      <LoanCardContent
        activitySectorTagsEnabled={activitySectorTagsEnabled}
        displayedGroups={displayedGroups}
        isDonation={isDonation}
        isMyListing={isMyListing}
        isService={isService}
        loan={loan}
        loanType={loanType}
        otherUser={otherUser}
      />
    </Pressable>
  );
}
